(function () {
  if (customElements.get('nuvos-sticky-bar')) return;

  class NuvosStickyBar extends HTMLElement {
    connectedCallback() {
      // Portal to <body> so position:fixed + z-index escape any ancestor stacking
      // context (the product details column is position:sticky on desktop, which would
      // otherwise trap the bar behind the header and product media).
      if (this.parentElement !== document.body) {
        if (!this.section) this.section = this.closest('.shopify-section');
        // Remove any stale portaled bar for the same product (e.g. editor re-render).
        document.querySelectorAll('body > nuvos-sticky-bar').forEach((el) => {
          if (el !== this && el.dataset.productId === this.dataset.productId) el.remove();
        });
        document.body.appendChild(this); // re-triggers connectedCallback with parent === body
        return;
      }
      if (this._initialized) return;
      this._initialized = true;

      this.inner = this.querySelector('[data-sb-inner]');
      this.productId = this.dataset.productId || '';
      this.mode = this.dataset.mode || 'add_to_cart';
      this.shown = false;
      this.forcedHiddenByFooter = false;
      this._cleanup = [];

      this.setupTopOffset();
      this.setupTrigger();
      this.setupFooterHide();
      this.setupVariantSync();
      this.setupButton();
      this.scheduleSwatchUpdate();
    }

    disconnectedCallback() {
      this._cleanup.forEach((fn) => fn());
      this._cleanup = [];
    }

    on(target, evt, handler, opts) {
      target.addEventListener(evt, handler, opts);
      this._cleanup.push(() => target.removeEventListener(evt, handler, opts));
    }

    show() {
      this.shown = true;
      this.refresh();
    }
    hide() {
      this.shown = false;
      this.refresh();
    }
    refresh() {
      if (!this.inner) return;
      const visible = this.shown && !this.forcedHiddenByFooter;
      this.inner.setAttribute('data-stuck', visible ? 'true' : 'false');
    }

    setupTopOffset() {
      if (this.dataset.position !== 'top') return;
      const measure = () => {
        const header =
          document.querySelector('#header-component') ||
          document.querySelector('.header') ||
          document.querySelector('header');
        let offset = 0;
        if (header) {
          // header bottom edge in viewport coords; for a sticky header this ~= its height.
          offset = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        }
        this.style.setProperty('--sb-top-offset', offset + 'px');
      };
      measure();
      let raf = null;
      const onChange = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          measure();
        });
      };
      this.on(window, 'scroll', onChange, { passive: true });
      this.on(window, 'resize', onChange, { passive: true });
    }

    setupTrigger() {
      const trigger = this.dataset.trigger || 'section';

      if (trigger === 'delay') {
        const sec = parseFloat(this.dataset.triggerDelay || '3');
        const t = setTimeout(() => this.show(), Math.max(0, sec) * 1000);
        this._cleanup.push(() => clearTimeout(t));
        return;
      }

      if (trigger === 'scroll') {
        const pct = parseFloat(this.dataset.triggerScroll || '30') / 100;
        let raf = null;
        const onScroll = () => {
          if (raf) return;
          raf = requestAnimationFrame(() => {
            raf = null;
            const max = document.documentElement.scrollHeight - window.innerHeight;
            const frac = max > 0 ? window.scrollY / max : 0;
            if (frac >= pct) this.show();
            else this.hide();
          });
        };
        this.on(window, 'scroll', onScroll, { passive: true });
        onScroll();
        return;
      }

      // 'section' (default): show once the product section is fully scrolled past the top.
      const target = this.section || this.parentElement;
      if (!target || !('IntersectionObserver' in window)) {
        this.show();
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          const e = entries[0];
          if (!e) return;
          const r = e.target.getBoundingClientRect();
          if (!e.isIntersecting && r.bottom <= 0) this.show();
          else if (e.isIntersecting) this.hide();
        },
        { threshold: 0 }
      );
      io.observe(target);
      this._cleanup.push(() => io.disconnect());
    }

    setupFooterHide() {
      const footer = document.querySelector('footer') || document.querySelector('[class*="footer-group"]');
      if (!footer || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        const e = entries[0];
        if (!e) return;
        this.forcedHiddenByFooter = e.isIntersecting;
        this.refresh();
      });
      io.observe(footer);
      this._cleanup.push(() => io.disconnect());
    }

    setupVariantSync() {
      const target = this.section || document;
      const onUpdate = (event) => {
        const detail = event.detail || {};
        const data = detail.data || {};
        if (data.productId && this.productId && String(data.productId) !== String(this.productId)) return;
        this.applyVariant(detail.resource);
      };
      const onSelected = (event) => {
        const id = event.detail && event.detail.resource && event.detail.resource.id;
        if (id) this.dataset.currentVariantId = id;
        this.scheduleSwatchUpdate();
      };
      this.on(target, 'variant:update', onUpdate);
      this.on(target, 'variant:selected', onSelected);
    }

    scheduleSwatchUpdate() {
      if (this._swatchRaf) return;
      this._swatchRaf = requestAnimationFrame(() => {
        this._swatchRaf = null;
        this.updateSwatch();
      });
    }

    // Mirror the variant picker's currently-selected swatch (single source of truth,
    // works for native swatches, the custom hex map, and image swatches).
    updateSwatch() {
      const swatchEl = this.querySelector('[data-sb-swatch]');
      if (!swatchEl) return;
      const scope = this.section || document;
      let picked = null;
      scope.querySelectorAll('input:checked').forEach((input) => {
        const label = input.closest('label');
        const sw = label && label.querySelector('.swatch');
        if (sw) picked = sw;
      });
      if (!picked) {
        swatchEl.hidden = true;
        return;
      }
      const value = getComputedStyle(picked).getPropertyValue('--swatch-background').trim();
      if (!value || value === 'null') {
        swatchEl.hidden = true;
        return;
      }
      swatchEl.style.setProperty('--swatch-background', value);
      swatchEl.hidden = false;
    }

    applyVariant(variant) {
      const variantEl = this.querySelector('[data-sb-variant]');
      const priceEl = this.querySelector('[data-sb-price]');
      const addBtn = this.querySelector('[data-sb-add]');

      if (variant == null) {
        if (addBtn) addBtn.setAttribute('data-sold-out', 'true');
        return;
      }
      this.dataset.currentVariantId = variant.id;

      if (variantEl && !variantEl.hasAttribute('hidden') && variant.title) {
        variantEl.textContent = variant.title;
      }
      if (priceEl && typeof variant.price === 'number') {
        priceEl.textContent = this.formatMoney(variant.price);
      }
      if (addBtn) {
        if (variant.available) addBtn.removeAttribute('data-sold-out');
        else addBtn.setAttribute('data-sold-out', 'true');
      }
      this.scheduleSwatchUpdate();
    }

    formatMoney(cents) {
      try {
        const cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: cur,
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }

    setupButton() {
      if (this.mode === 'email') return; // popup handled by nuvos-waitlist.js
      const addBtn = this.querySelector('[data-sb-add]');
      if (!addBtn) return;
      this.on(addBtn, 'click', () => this.puppetAddToCart());
    }

    puppetAddToCart() {
      let realBtn = null;
      if (this.section) {
        realBtn = this.section.querySelector(
          `product-form-component[data-product-id="${this.productId}"] [ref="addToCartButton"]`
        );
      }
      if (!realBtn) {
        realBtn = document.querySelector(
          `product-form-component[data-product-id="${this.productId}"] [ref="addToCartButton"]`
        );
      }
      if (realBtn) {
        realBtn.dataset.puppet = 'true';
        realBtn.click();
        return;
      }
      // Defensive fallback: direct cart add with the current variant id.
      const id = this.dataset.currentVariantId;
      if (!id) return;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      }).then(() => {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      });
    }
  }

  customElements.define('nuvos-sticky-bar', NuvosStickyBar);
})();
